import { withBreadCrumbs } from "lego-gui-lib";
import { inject } from "mobx-react";
import { observer } from "mobx-react-lite";
import React from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch
} from "react-router";
import {
  TabContentContainer,
  TabTitleContainer,
  TabViewContainer
} from "../../common/tabContainers";
import { EditIvrMenuSchema, IvrMenuSchema } from "./schema";
import { CreateView, IndexView, ShowView } from "lego-gui-lib/lib";
import {
  IvrMenuUserInputIndex,
  IvrMenuUserInputCreate,
  IvrMenuUserInputEdit
} from "./userInputs/views";
import { ParentRoute } from "../../core/model/parent";
import { NestedView } from "../../common/nestedViewComponent";
import { CustomerAwareRowButtons } from "../../common/CustomerAwareRowButtons";
import { isAllowed } from "../../common/common";
import i18next from "i18next";

export const IvrMenuViewComponent = () => {
  const match = useRouteMatch();
  const baseUrl = match.path;
  const title = i18next.t("hns:ivrMenu:domain_plural");

  return withBreadCrumbs(
    { to: baseUrl, children: title },
    <>
      <ParentRoute path={baseUrl + "/:id"} dataSourceName={"ivrMenu"} />
      <Switch>
        <Route exact={true} path={baseUrl} component={Index} />
        <Route
          path={baseUrl + "/:id/userInputs/create/:userInputId"}
          component={IvrMenuUserInputCreate}
        />
        <Route
          path={baseUrl + "/:id/userInputs/create"}
          component={IvrMenuUserInputCreate}
        />
        <Route
          path={baseUrl + "/:id/userInputs/:userInputId"}
          component={IvrMenuUserInputEdit}
        />
        <Redirect from={baseUrl + "/:id/userInputs"} to={baseUrl + "/:id"} />
        <Route path={baseUrl + "/create/:id"} component={Clone} />
        <Route path={baseUrl + "/create"} component={Create} />
        <Route path={baseUrl + "/:id"} component={Show} />
      </Switch>
    </>
  );
};

const injectDataSource = (comp: any) =>
  inject(
    "ivrMenu",
    "ivrMenuForCopy",
    "ivrMenuUserInput",
    "auth",
    "i18n"
  )(observer(comp));

const IndexSchema = IvrMenuSchema;
export const Index = injectDataSource(({ ivrMenu, i18n, ...rest }: any) => {
  const title = i18n.t("hns:ivrMenu:domain", { count: 2 });
  return (
    <IndexView
      dataSource={ivrMenu.listDataSource}
      {...rest}
      title={title}
      toolbarButtons={{ delete: true, export: true }}
      rowButtons={CustomerAwareRowButtons}
      columns={IndexSchema}
      titleVariant={"h5"}
      titleContainer={TabTitleContainer}
      viewContainer={TabViewContainer}
      contentContainer={TabContentContainer}
    />
  );
});

export const Create = injectDataSource(
  ({ ivrMenu, i18n, match, auth, ...rest }: any) => {
    const title = i18n.t("common:create", { domain: "hns:ivrMenu:domain" });
    const baseUrl = match.url;
    const history = useHistory();
    return withBreadCrumbs(
      { to: baseUrl + "/create", children: title },
      <CreateView
        dataSource={ivrMenu.oneDataSource}
        {...rest}
        title={title}
        initialValues={{
          maxAttempts: 3
        }}
        fields={EditIvrMenuSchema}
        id={match.params}
        goBack={() => history.goBack()}
      />
    );
  }
);

export const Show = injectDataSource((props: any) => {
  const { ivrMenu, match, auth, i18n, ...rest } = props;
  const baseUrl = match.url;
  const allowed = isAllowed(ivrMenu);
  const title = allowed
    ? i18n.t("common:edit", { domain: "hns:ivrMenu:domain" })
    : i18n.t("hns:ivrMenu:domain");
  const fields = allowed
    ? EditIvrMenuSchema
    : EditIvrMenuSchema.makeAllReadonly();
  const history = useHistory();
  return withBreadCrumbs(
    { to: baseUrl + "/edit", children: title },
    <NestedView
      showView={
        <ShowView
          dataSource={ivrMenu.oneDataSource}
          hideDeleteButton={!allowed}
          title={title}
          fields={fields}
          id={match.params}
          preloadData={false}
          onlyView={!allowed}
          goBack={() => history.goBack()}
        />
      }
      indexView={
        <IvrMenuUserInputIndex match={match} {...rest} isAllowed={allowed} />
      }
    />
  );
});

export const Clone = injectDataSource(
  ({ ivrMenuForCopy, language, match, auth, i18n, ...rest }: any) => {
    // const name = useFirstValue(ivrMenu.oneDataSource.one, ds => ds.name);
    const title = i18n.t("common:create", {
      domain: "hns:ivrMenu:domain"
    });
    const history = useHistory();
    return withBreadCrumbs(
      { to: match.url + "/edit", children: i18n.t("common:copy") },
      <CreateView
        submit={async () => {
          return ivrMenuForCopy.oneDataSource.clone();
        }}
        dataSource={ivrMenuForCopy.oneDataSource}
        {...rest}
        title={title}
        fields={IvrMenuSchema}
        id={match.params}
        goBack={() => history.goBack()}
      />
    );
  }
);
